import { useEffect, useMemo, useRef, useState } from "react";
import mapService from "services/mapService";
import type { PointBounds } from "services/map/MapPointQuery";
import { mapper, type IpPoint } from "view/Map/MapModel";

const EMPTY_POINTS: IpPoint[] = [];

interface AreaResult {
  key: string;
  points: IpPoint[];
  error: string | null;
}

export const useViewportPoints = (categoryId: number, locale: string, bounds: PointBounds | null) => {
  const enabled = categoryId !== 10 && categoryId !== 11;
  const key = `${locale}:${categoryId}:${bounds?.join(":")}`;
  const cache = useRef(new Map<string, { points: IpPoint[]; loadedAt: number }>());
  const [result, setResult] = useState<AreaResult>({ key: "", points: [], error: null });
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const area = useMemo(() => bounds, [key]);

  useEffect(() => {
    if (!enabled || !area) return;
    const cached = cache.current.get(key);
    if (cached && Date.now() - cached.loadedAt < 60000) {
      setResult({ key, points: cached.points, error: null });
      setPendingKey(null);
      return;
    }
    const controller = new AbortController();
    setPendingKey(key);
    mapService.getIpPoints(categoryId, locale, area, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        const points = response.data.map((entry) => ({
          ...entry.attributes,
          id: String(entry.id),
          icon: mapper[categoryId].icon,
          latitudine: Number(entry.attributes.latitudine),
          longitudine: Number(entry.attributes.longitudine),
        } as IpPoint));
        cache.current.delete(key);
        cache.current.set(key, { points, loadedAt: Date.now() });
        if (cache.current.size > 12) cache.current.delete(cache.current.keys().next().value);
        setResult({ key, points, error: null });
      })
      .catch(() => {
        if (!controller.signal.aborted) setResult({ key, points: [], error: locale === "ro" ? "Nu s-au putut încărca punctele din această zonă." : "Failed to load points in this area." });
      })
      .finally(() => {
        if (!controller.signal.aborted) setPendingKey(null);
      });
    return () => controller.abort();
  }, [area, categoryId, enabled, key, locale]);

  return {
    points: result.key === key ? result.points : EMPTY_POINTS,
    loading: Boolean(enabled && area && (result.key !== key || pendingKey === key)),
    error: result.key === key ? result.error : null,
  };
};
